/**
 * K2 and Swedish Accounting Constants
 */
// Swedish corporate tax rate (2024-2025)
export const CORPORATE_TAX_RATE = 0.206;
// Factor for calculating adjusted equity from untaxed reserves
export const UNTAXED_RESERVES_EQUITY_FACTOR = 1 - CORPORATE_TAX_RATE; // 0.794
// Minimum share capital for private limited company (AB)
export const MINIMUM_SHARE_CAPITAL_AB = 25000;
// K2 eligibility thresholds (BFNAR 2006:11)
export const K2_THRESHOLDS = {
    maxEmployees: 50,
    maxTotalAssets: 40_000_000, // 40 MSEK
    maxNetRevenue: 80_000_000, // 80 MSEK
};
// Fiscal year length limits (BFL 3 kap)
export const FISCAL_YEAR = {
    minMonths: 1,
    maxMonths: 18,
};
// Balance tolerance for validation (kr)
export const BALANCE_TOLERANCE = 1;
// Multi-year overview requirements
export const MULTI_YEAR_OVERVIEW_YEARS = 4;
// BAS Account ranges
export const BAS_ACCOUNT_RANGES = {
    assets: { start: "1000", end: "1999" },
    fixedAssets: { start: "1000", end: "1399" },
    currentAssets: { start: "1400", end: "1999" },
    equityAndLiabilities: { start: "2000", end: "2999" },
    equity: { start: "2000", end: "2099" },
    untaxedReserves: { start: "2100", end: "2199" },
    provisions: { start: "2200", end: "2299" },
    longTermLiabilities: { start: "2300", end: "2399" },
    shortTermLiabilities: { start: "2400", end: "2999" },
    income: { start: "3000", end: "3999" },
    expenses: { start: "4000", end: "7999" },
    financialItems: { start: "8000", end: "8799" },
    appropriations: { start: "8800", end: "8899" },
    taxes: { start: "8900", end: "8999" },
};
// Specific account numbers
export const SPECIFIC_ACCOUNTS = {
    shareCapital: "2081",
    yearResult: "2099",
    previousYearResult: "2098",
    legalReserve: "2086",
    cashAndBank: "1910",
    accountsReceivable: "1510",
    accountsPayable: "2440",
    // Account 8999 is a technical closing account used to balance the income statement
    // It should be EXCLUDED from result calculations as it merely transfers the result to balance sheet
    yearResultClosing: "8999",
};
// K2 ineligibility rules
export const K2_INELIGIBILITY_REASONS = [
    "PUBLIC_COMPANY",
    "LARGER_GROUP_PARENT",
    "PREPARES_CONSOLIDATED_ACCOUNTS",
    "NATURAL_PERSON",
    "BRF_AFTER_2026",
    "FOREIGN_BRANCH_AFTER_2026",
    "CRYPTO_ASSETS_AFTER_2026",
    "SHARE_BASED_PAYMENTS_AFTER_2026",
];
// ═══════════════════════════════════════════════════════════════════
// Company Membership Roles
// ═══════════════════════════════════════════════════════════════════
/**
 * Available membership roles for company members
 * These are SEPARATE from admin roles (which handle system-wide access)
 */
export const MEMBERSHIP_ROLES = ["owner", "manager", "editor", "viewer"];
/**
 * Role information for UI display
 */
export const ROLE_INFO = {
    owner: {
        label: "Ägare",
        description: "Full kontroll. Kan hantera allt inklusive företagsinställningar och betalning.",
    },
    manager: {
        label: "Administratör",
        description: "Kan hantera medlemmar och årsredovisningar, men inte företagsinställningar.",
    },
    editor: {
        label: "Redigerare",
        description: "Kan skapa och redigera årsredovisningar, men inte hantera medlemmar.",
    },
    viewer: {
        label: "Läsbehörighet",
        description: "Kan endast visa information. Kan inte göra några ändringar.",
    },
};
/**
 * Role labels for compact display
 */
export const ROLE_LABELS = {
    owner: "Ägare",
    manager: "Administratör",
    editor: "Redigerare",
    viewer: "Läsbehörighet",
};
/**
 * Role hierarchy (lower number = higher privileges)
 * Used for sorting and determining relative privilege levels
 */
export const ROLE_HIERARCHY = {
    owner: 1,
    manager: 2,
    editor: 3,
    viewer: 4,
};
